import React, {Suspense, useEffect, useRef} from 'react'
import {useLocation} from 'react-router-dom'
import {isNewAuthSystemEnabled} from '@apis/auth'
import {useAuthContext} from '@containers/App/AuthProvider'
import type {AuthContextProps} from '@containers/App/AuthProvider'
import AuthTemplateLoader from '@shared/components/loaders/dashboards/AuthTemplate'
import DashTemplateLoader from '@shared/components/loaders/dashboards/DashTemplate'
import {
  buildAuthorizeUrl,
  clearForceLogin,
  isForceLoginRequested,
} from '@shared/utils/buildAuthorizeUrl'

interface AuthRedirectProps {
  children?: React.ReactNode
}

const AuthRedirect: React.FC<AuthRedirectProps> = ({children}) => {
  const location = useLocation()
  const {userIsLoggedIn} = useAuthContext() as AuthContextProps
  const redirectTriggeredRef = useRef(false)
  const isAuthRoute = location.pathname === '/login' || location.pathname === '/create-account'

  useEffect(() => {
    if (!isNewAuthSystemEnabled || userIsLoggedIn || redirectTriggeredRef.current) {
      return
    }

    redirectTriggeredRef.current = true

    const redirectToProvider = async (): Promise<void> => {
      // Sent on to the provider as the OAuth state parameter and handed back to
      // navigate() on return, so it is validated here too (AB#4528) — done
      // inside buildAuthorizeUrl now, but the raw value is built the same way.
      const originalUrl =
        sessionStorage.getItem('originalUrl') || location.pathname + location.search + location.hash
      // After a logout the auth site must show the form even if its session
      // survived a timed-out revoke. One shot: cleared once the URL is built,
      // kept for the retry if building it failed (AB#6617).
      const loginUrl = await buildAuthorizeUrl(originalUrl, {forceLogin: isForceLoginRequested()})
      clearForceLogin()

      window.location.assign(loginUrl)
    }

    void redirectToProvider().catch((error) => {
      console.error('Failed to initialize auth redirect:', error)
      redirectTriggeredRef.current = false
    })
  }, [location, userIsLoggedIn])

  return (
    <Suspense fallback={isAuthRoute ? <AuthTemplateLoader /> : <DashTemplateLoader />}>
      {children || (isAuthRoute ? <AuthTemplateLoader /> : <DashTemplateLoader />)}
    </Suspense>
  )
}

export default AuthRedirect
